import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Button } from '@/components/atoms/Button';
import { Switch } from '@/components/atoms/Switch';
import { Select } from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import { Bell, FileText, Eye, Save } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { ApiClient } from '@/services/api/client';
import styles from './SettingsPage.module.scss';

const DEFAULT_NOTIFICATIONS = {
  emailNotifications: true,
  newEmployeeAlerts: true,
  weeklyReports: true,
  lowActivityAlerts: true,
  achievementAlerts: false,
};

const DEFAULT_REPORTS = {
  autoGenerateReports: true,
  reportFrequency: 'weekly',
  includeInactiveEmployees: false,
  detailedMetrics: true,
};

const DEFAULT_DISPLAY = {
  showEmployeePhotos: true,
  defaultView: 'grid',
  itemsPerPage: '20',
};

export function SettingsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATIONS);
  const [reportSettings, setReportSettings] = useState(DEFAULT_REPORTS);
  const [displaySettings, setDisplaySettings] = useState(DEFAULT_DISPLAY);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      try {
        const prefs = (await ApiClient.get<Record<string, unknown>>('/users/me/preferences').catch(
          (): Record<string, unknown> => ({})
        ));
        setNotificationSettings({
          emailNotifications: (prefs['emailNotifications'] as boolean) ?? DEFAULT_NOTIFICATIONS.emailNotifications,
          newEmployeeAlerts: (prefs['newEmployeeAlerts'] as boolean) ?? DEFAULT_NOTIFICATIONS.newEmployeeAlerts,
          weeklyReports: (prefs['weeklyReports'] as boolean) ?? DEFAULT_NOTIFICATIONS.weeklyReports,
          lowActivityAlerts: (prefs['lowActivityAlerts'] as boolean) ?? DEFAULT_NOTIFICATIONS.lowActivityAlerts,
          achievementAlerts: (prefs['achievementAlerts'] as boolean) ?? DEFAULT_NOTIFICATIONS.achievementAlerts,
        });
        setReportSettings({
          autoGenerateReports: (prefs['autoGenerateReports'] as boolean) ?? DEFAULT_REPORTS.autoGenerateReports,
          reportFrequency: (prefs['reportFrequency'] as string) ?? DEFAULT_REPORTS.reportFrequency,
          includeInactiveEmployees: (prefs['includeInactiveEmployees'] as boolean) ?? DEFAULT_REPORTS.includeInactiveEmployees,
          detailedMetrics: (prefs['detailedMetrics'] as boolean) ?? DEFAULT_REPORTS.detailedMetrics,
        });
        setDisplaySettings({
          showEmployeePhotos: (prefs['showEmployeePhotos'] as boolean) ?? DEFAULT_DISPLAY.showEmployeePhotos,
          defaultView: (prefs['defaultView'] as string) ?? DEFAULT_DISPLAY.defaultView,
          itemsPerPage: (prefs['itemsPerPage'] as string) ?? DEFAULT_DISPLAY.itemsPerPage,
        });
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadSettings();
     
  }, [user?.id, location.pathname]);

  const handleSaveSettings = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    try {
      await ApiClient.patch('/users/me/preferences', {
        ...notificationSettings,
        ...reportSettings,
        ...displaySettings,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.settings}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem' }}>
          <Spinner size="lg" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.settings__header}>
        <div>
          <h1 className={styles.settings__title}>Settings</h1>
          <p className={styles.settings__subtitle}>Manage your preferences and configurations</p>
        </div>
        <Button
          variant="primary"
          onClick={handleSaveSettings}
          isLoading={isSaving}
          leftIcon={<Save size={16} />}
        >
          Save Changes
        </Button>
      </div>

      <div className={styles.settings__content}>
        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Bell className={styles.settings__cardIcon} />
              <CardTitle>Notification Preferences</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Email Notifications</span>
                  <span className={styles.settings__itemDescription}>
                    Receive email updates about your team's activity
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.emailNotifications}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, emailNotifications: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>New Employee Alerts</span>
                  <span className={styles.settings__itemDescription}>
                    Get notified when new employees join or complete onboarding
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.newEmployeeAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, newEmployeeAlerts: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Weekly Reports</span>
                  <span className={styles.settings__itemDescription}>
                    Receive weekly progress summaries via email
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.weeklyReports}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, weeklyReports: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Low Activity Alerts</span>
                  <span className={styles.settings__itemDescription}>
                    Get alerted when employees haven't been active recently
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.lowActivityAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, lowActivityAlerts: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Achievement Alerts</span>
                  <span className={styles.settings__itemDescription}>
                    Receive notifications when employees reach milestones
                  </span>
                </div>
                <Switch
                  checked={notificationSettings.achievementAlerts}
                  onCheckedChange={(checked) =>
                    setNotificationSettings({ ...notificationSettings, achievementAlerts: checked })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <FileText className={styles.settings__cardIcon} />
              <CardTitle>Report Configuration</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Auto-Generate Reports</span>
                  <span className={styles.settings__itemDescription}>
                    Automatically generate reports based on the frequency below
                  </span>
                </div>
                <Switch
                  checked={reportSettings.autoGenerateReports}
                  onCheckedChange={(checked) =>
                    setReportSettings({ ...reportSettings, autoGenerateReports: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Report Frequency</span>
                  <span className={styles.settings__itemDescription}>
                    How often to generate automatic reports
                  </span>
                </div>
                <Select
                  value={reportSettings.reportFrequency}
                  onValueChange={(value) =>
                    setReportSettings({ ...reportSettings, reportFrequency: value })
                  }
                  options={[
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                  ]}
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Include Inactive Employees</span>
                  <span className={styles.settings__itemDescription}>
                    Show employees with no recent activity in reports
                  </span>
                </div>
                <Switch
                  checked={reportSettings.includeInactiveEmployees}
                  onCheckedChange={(checked) =>
                    setReportSettings({ ...reportSettings, includeInactiveEmployees: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Detailed Metrics</span>
                  <span className={styles.settings__itemDescription}>
                    Include detailed performance metrics in reports
                  </span>
                </div>
                <Switch
                  checked={reportSettings.detailedMetrics}
                  onCheckedChange={(checked) =>
                    setReportSettings({ ...reportSettings, detailedMetrics: checked })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className={styles.settings__cardHeader}>
              <Eye className={styles.settings__cardIcon} />
              <CardTitle>Display Preferences</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={styles.settings__section}>
              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Show Employee Photos</span>
                  <span className={styles.settings__itemDescription}>
                    Display employee profile pictures in lists and tables
                  </span>
                </div>
                <Switch
                  checked={displaySettings.showEmployeePhotos}
                  onCheckedChange={(checked) =>
                    setDisplaySettings({ ...displaySettings, showEmployeePhotos: checked })
                  }
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Default View</span>
                  <span className={styles.settings__itemDescription}>
                    Default layout for employee lists
                  </span>
                </div>
                <Select
                  value={displaySettings.defaultView}
                  onValueChange={(value) =>
                    setDisplaySettings({ ...displaySettings, defaultView: value })
                  }
                  options={[
                    { value: 'grid', label: 'Grid View' },
                    { value: 'list', label: 'List View' },
                    { value: 'table', label: 'Table View' },
                  ]}
                />
              </div>

              <div className={styles.settings__item}>
                <div className={styles.settings__itemInfo}>
                  <span className={styles.settings__itemLabel}>Items Per Page</span>
                  <span className={styles.settings__itemDescription}>
                    Number of items to show per page in tables
                  </span>
                </div>
                <Select
                  value={displaySettings.itemsPerPage}
                  onValueChange={(value) =>
                    setDisplaySettings({ ...displaySettings, itemsPerPage: value })
                  }
                  options={[
                    { value: '10', label: '10 items' },
                    { value: '20', label: '20 items' },
                    { value: '50', label: '50 items' },
                    { value: '100', label: '100 items' },
                  ]}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SettingsPage;
